import type { ISerialize } from '@/objects/ISerialize';

export class ContestantImportDefaultSettings implements ISerialize {
    public ColumnCaptions: string[];

    public DateFormat: string;

    public GenderFemale: string;

    public GenderMale: string;

    public GenderNeutral: string | null;

    public GenerateCategories: boolean;

    public GenerateGroups: boolean;

    public HideAdvancedOptions: boolean;

    public IgnoreAllreadyImported: boolean;

    public LockHeader: boolean;

    public ReadOnly: boolean;

    public ShowFilter: boolean;

    public ShowToolbar: boolean;

    public SkipExistingContestants: boolean;

    public TimeFormat: string;

    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ColumnCaptions?: string[];
        DateFormat?: string | null;
        GenderFemale?: string | null;
        GenderMale?: string | null;
        GenderNeutral?: string | null;
        GenerateCategories?: boolean;
        GenerateGroups?: boolean;
        HideAdvancedOptions?: boolean;
        IgnoreAllreadyImported?: boolean;
        LockHeader?: boolean;
        ReadOnly?: boolean;
        ShowFilter?: boolean;
        ShowToolbar?: boolean;
        SkipExistingContestants?: boolean;
        TimeFormat?: string | null;
    }) {
        this.ColumnCaptions = values.ColumnCaptions ?? [];

        this.DateFormat = values.DateFormat ?? '';

        this.GenderFemale = values.GenderFemale ?? '';

        this.GenderMale = values.GenderMale ?? '';

        this.GenderNeutral = values.GenderNeutral ?? null;

        this.GenerateCategories = values.GenerateCategories ?? false;

        this.GenerateGroups = values.GenerateGroups ?? false;

        this.HideAdvancedOptions = values.HideAdvancedOptions ?? false;

        this.IgnoreAllreadyImported = values.IgnoreAllreadyImported ?? false;

        this.LockHeader = values.LockHeader ?? false;

        this.ReadOnly = values.ReadOnly ?? false;

        this.ShowFilter = values.ShowFilter ?? false;

        this.ShowToolbar = values.ShowToolbar ?? false;

        this.SkipExistingContestants = values.SkipExistingContestants ?? false;

        this.TimeFormat = values.TimeFormat ?? '';
    }

    public serialize(): string {
        return (
            '{' +
            `"ColumnCaptions": [ ${this.ColumnCaptions.map((x) => JSON.stringify(x))} ]` +
            ',' +
            `"DateFormat": ${JSON.stringify(this.DateFormat)}` +
            ',' +
            `"GenderFemale": ${JSON.stringify(this.GenderFemale)}` +
            ',' +
            `"GenderMale": ${JSON.stringify(this.GenderMale)}` +
            ',' +
            `"GenderNeutral": ${JSON.stringify(this.GenderNeutral)}` +
            ',' +
            `"GenerateCategories": ${JSON.stringify(this.GenerateCategories)}` +
            ',' +
            `"GenerateGroups": ${JSON.stringify(this.GenerateGroups)}` +
            ',' +
            `"HideAdvancedOptions": ${JSON.stringify(this.HideAdvancedOptions)}` +
            ',' +
            `"IgnoreAllreadyImported": ${JSON.stringify(this.IgnoreAllreadyImported)}` +
            ',' +
            `"LockHeader": ${JSON.stringify(this.LockHeader)}` +
            ',' +
            `"ReadOnly": ${JSON.stringify(this.ReadOnly)}` +
            ',' +
            `"ShowFilter": ${JSON.stringify(this.ShowFilter)}` +
            ',' +
            `"ShowToolbar": ${JSON.stringify(this.ShowToolbar)}` +
            ',' +
            `"SkipExistingContestants": ${JSON.stringify(this.SkipExistingContestants)}` +
            ',' +
            `"TimeFormat": ${JSON.stringify(this.TimeFormat)}` +
            '}'
        );
    }

    /**
     * Creates an instance of ContestantImportDefaultSettings from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ColumnCaptions?: string[];
        DateFormat?: string | null;
        GenderFemale?: string | null;
        GenderMale?: string | null;
        GenderNeutral?: string | null;
        GenerateCategories?: boolean;
        GenerateGroups?: boolean;
        HideAdvancedOptions?: boolean;
        IgnoreAllreadyImported?: boolean;
        LockHeader?: boolean;
        ReadOnly?: boolean;
        ShowFilter?: boolean;
        ShowToolbar?: boolean;
        SkipExistingContestants?: boolean;
        TimeFormat?: string | null;
    }): ContestantImportDefaultSettings {
        return new ContestantImportDefaultSettings(values);
    }

    public static _FromServerJson(json: any): ContestantImportDefaultSettings {
        return new ContestantImportDefaultSettings({
            ColumnCaptions: json.ColumnCaptions,
            DateFormat: json.DateFormat ?? '',
            GenderFemale: json.GenderFemale ?? '',
            GenderMale: json.GenderMale ?? '',
            GenderNeutral: json.GenderNeutral ?? '',
            GenerateCategories: json.GenerateCategories,
            GenerateGroups: json.GenerateGroups,
            HideAdvancedOptions: json.HideAdvancedOptions,
            IgnoreAllreadyImported: json.IgnoreAllreadyImported,
            LockHeader: json.LockHeader,
            ReadOnly: json.ReadOnly,
            ShowFilter: json.ShowFilter,
            ShowToolbar: json.ShowToolbar,
            SkipExistingContestants: json.SkipExistingContestants,
            TimeFormat: json.TimeFormat ?? '',
        });
    }

    /**
     * Creates an instance of ContestantImportDefaultSettings from another instance of ContestantImportDefaultSettings.
     * @param other The ContestantImportDefaultSettings you want to clone
     * @returns A cloned ContestantImportDefaultSettings
     */
    public static FromOther(other: ContestantImportDefaultSettings): ContestantImportDefaultSettings {
        return ContestantImportDefaultSettings._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return ContestantImportDefaultSettings.FromOther(this);
    }

    static NAME_OF_CLASS = 'ContestantImportDefaultSettings';
}
